import React, {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import {
  notEmptyString,
} from '@populateui/utils';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import MonthPicker from 'core/components/DateTimePicker/MonthPicker';
import SummaryCard, {
  SummaryCardItem,
} from 'core/components/SummaryCard';
import ToolTip from 'core/components/Tooltip';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import {
  useMedia,
  useSidePanel,
  useTableReset,
} from 'core/hooks';
import SearchIcon from 'core/icons/search';
import SettingsIcon from 'core/icons/settings';
import useStorageState from 'core/hooks/storage';
import {
  PCCDashboardPillCountsDTO,
  PCCPatientDetailsRequestDTO,
  PCCPatientDetailsResponseDTO,
} from 'dtos';
import {
  PCCDashboardPillFilter,
} from 'dtos/pCCDashboardPillFilter';
import {
  postApiPCCDashboardGetPCCPatientDetails,
} from 'endpoints';
import Button from 'pages/Dashboard/components/Button';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  useSyncDateRange,
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import {
  useGetPCCActivationSettings,
} from 'views/EMR/hooks/pcc';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import getColumns from 'pages/Dashboard/pages/Dashboard/PCCOverview/table/columns';
import {
  normalizePCCOverviewRows,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/helper';
import {
  PCC_MONTH_FORMAT,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/pccPanelUrl';
import {
  PCCOverviewRow,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import {
  patientAlertsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';
import dayjs from 'utils/dayjs';
import useSignalREffect from 'views/EMR/hooks/signalR';

type PCCSummaryFilter = Omit<SummaryCardItem, 'id' | 'value'> & {
  id: PCCDashboardPillFilter;
  countKey: keyof PCCDashboardPillCountsDTO;
  supportsDateRange: boolean;
};

type PCCSummaryCardItem = SummaryCardItem & {
  supportsDateRange: boolean;
};

type OverviewProps = {
  tableInstanceRef: RefObject<VirtualTableInstance>;
  selectedPill: PCCSummaryFilter;
  dateRange: DateRange;
  onSelect: (id: string) => void;
};

const DEFAULT_PCC_SUMMARY_FILTER: PCCSummaryFilter = {
  id: PCCDashboardPillFilter.Enrolled,
  label: 'Enrolled',
  countKey: 'enrolled',
  tone: 'default',
  supportsDateRange: true,
};

const pccSummaryItems: PCCSummaryFilter[] = [
  DEFAULT_PCC_SUMMARY_FILTER,
  {
    id: PCCDashboardPillFilter.NeedsAttention,
    label: 'Due this Cycle',
    countKey: 'needsAttention',
    tone: 'danger',
    supportsDateRange: true,
  },
  {
    id: PCCDashboardPillFilter.WaitingForConsent,
    label: 'Waiting for Consent',
    countKey: 'waitingForConsent',
    tone: 'warning',
    supportsDateRange: true,
  },
  {
    id: PCCDashboardPillFilter.EligibleNotEnrolled,
    label: 'Eligible Not Enrolled',
    countKey: 'eligibleNotEnrolled',
    tone: 'info',
    supportsDateRange: false,
  },
];

const pccSummaryItemsMapsByID = keyBy(pccSummaryItems, 'id');

const defaultPccOverviewDateRange: DateRange = {
  min: dayjs().startOf('month').toDate(),
  max: dayjs().endOf('month').toDate(),
};

function getSummaryItems(
  pillCounts?: PCCDashboardPillCountsDTO,
): PCCSummaryCardItem[] {
  return pccSummaryItems.map(({ countKey, ...filter }) => ({
    ...filter,
    value: pillCounts?.[countKey] ?? 0,
  }));
}

function Overview({
  tableInstanceRef,
  selectedPill,
  dateRange,
  onSelect,
}: OverviewProps) {
  const {
    data,
    isFetched,
    auxiliary,
    refetch,
  } = useContext(DataContext);
  const { requestMedia } = useMedia();
  const { openPanel } = useSidePanel();
  const { canRequestMultimedia } = useEMRAccess();
  const { data: activationSettings } = useGetPCCActivationSettings();
  const isCcmApplicable = activationSettings?.isCcmApplicable !== false;
  const [pillCounts, setPillCounts] = useState<PCCDashboardPillCountsDTO | undefined>();
  const items = getSummaryItems(pillCounts);
  const dateScopedItems = items.filter(({ supportsDateRange }) => supportsDateRange);
  const allTimeItems = items.filter(({ supportsDateRange }) => !supportsDateRange);
  const columns = useMemo(
    () => getColumns(isCcmApplicable),
    [isCcmApplicable],
  );

  const openPCCSidePanel = useCallback((row: PCCOverviewRow) => {
    const patientId = row.patient?.patientId;

    if (!isNil(patientId) && patientId > 0) {
      openPanel('pcc', {
        patientId,
        month: dayjs(dateRange.min).format(PCC_MONTH_FORMAT),
      });
    }
  }, [dateRange, openPanel]);

  useEffect(() => {
    if (!isNil(auxiliary)) {
      setPillCounts(auxiliary as PCCDashboardPillCountsDTO);
    }
  }, [auxiliary]);

  useEffect(() => {
    if (isFetched && !isNil(data) && canRequestMultimedia) {
      const patientIds = compact(
        (data ?? []).map(({ patient }) => patient?.patientId),
      );
      if (patientIds.length > 0) {
        requestMedia(patientIds);
      }
    }
  }, [isFetched, data, canRequestMultimedia, requestMedia]);

  const debouncedRefetch = useMemo(
    () => debounce(() => {
      if (!isNil(refetch)) {
        refetch();
      }
    }, 100),
    [refetch],
  );

  useEffect(() => () => {
    debouncedRefetch.cancel();
  }, [debouncedRefetch]);

  useEffect(() => {
    const unsubscribe = patientAlertsEventChannel.on(
      'onPatientAlertsChanged',
      (targetPatientId) => {
        if (isNil(targetPatientId)) {
          return;
        }

        const isVisiblePatient = (data as PCCOverviewRow[] | undefined)?.some(
          (row) => row.patient?.patientId === targetPatientId,
        );

        if (isVisiblePatient) {
          debouncedRefetch();
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [data, debouncedRefetch]);

  useSignalREffect('OnPCCCommuniqueUpdated', () => {
    debouncedRefetch();
  }, [debouncedRefetch]);

  return (
    <div className="relative flex flex-col flex-1 overflow-y-hidden text-gray-600">
      <div className="px-4 pb-3">
        <div className="flex items-stretch">
          <section
            aria-label="Date-scoped PCC filters"
            className="min-w-0 flex-[3]"
          >
            <SummaryCard
              items={dateScopedItems}
              selectedId={selectedPill.id}
              onSelect={onSelect}
              isLoading={isNil(pillCounts)}
              skeletonItemCount={3}
            />
          </section>
          <div
            aria-hidden
            className="mx-3 w-px shrink-0 bg-gray-200"
          />
          <section
            aria-label="All-time PCC filters"
            className="min-w-0 flex-1"
          >
            <SummaryCard
              items={allTimeItems}
              selectedId={selectedPill.id}
              onSelect={onSelect}
              isLoading={isNil(pillCounts)}
              skeletonItemCount={1}
            />
          </section>
        </div>
      </div>
      <div className="w-full flex-1 min-h-0">
        <Table<PCCOverviewRow>
          instanceRef={tableInstanceRef}
          columns={columns}
          getRowId={(row) => row.rowKey}
          onRowClick={(_table, row) => {
            openPCCSidePanel(row.original);
          }}
          emptySlot="No PCC patients found"
          loadingSlot="Loading PCC patients..."
          classes={{
            header: '!justify-start px-4',
            outerColumn: '!items-start',
            column: '!text-left',
            outerRow: '!w-[calc(100%-16px)] left-4 cursor-pointer',
            row: '!items-stretch !pt-1 !justify-start',
            cell: '!items-start !h-auto !py-2',
          }}
        />
      </div>
    </div>
  );
}

export default function PCCOverview() {
  const { openPanel } = useSidePanel();
  const { isPCCActionAvailable } = useEMRAccess();
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery({ storageKey: 'pccOverviewSearchQuery' });
  const [selectedPillId, setSelectedPillId] = useStorageState<PCCDashboardPillFilter>(
    'pccOverviewSelectedPill',
    DEFAULT_PCC_SUMMARY_FILTER.id,
  );
  const selectedPill = pccSummaryItemsMapsByID[selectedPillId]
    ?? DEFAULT_PCC_SUMMARY_FILTER;
  const [dateRange, setDateRange] = useSyncDateRange({
    uniqueKey: 'pccOverviewDateRange',
    defaultDateRange: defaultPccOverviewDateRange,
  });
  const [defaultPagination] = useStorageState<ReactTablePaginationState>('pccOverviewPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  function onSelectSummaryCard(id: string) {
    setSelectedPillId(id as PCCDashboardPillFilter);
    reset();
  }

  function handleOpenPCCSettings() {
    openPanel('pccActivationSettings', {});
  }

  return (
    <div className="flex h-full min-w-0 w-full flex-col gap-4">
      <div className="min-w-0 w-full shrink-0 overflow-x-auto px-4 py-2 -my-2">
        <div className="flex w-full min-w-max items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <SearchInput
              placeholder="Enter patient name, phone or date of birth"
              className="w-96"
              delay={400}
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                reset();
              }}
              inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
              icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
              id="pcc-patient-search"
            />
            <ToolTip
              title="Date range not applicable"
              hide={selectedPill.supportsDateRange}
            >
              <div>
                <MonthPicker
                  disabled={!selectedPill.supportsDateRange}
                  value={dayjs(dateRange.min)}
                  placeholder="MM/YYYY"
                  onChange={(value) => {
                    if (!isNil(value)) {
                      setDateRange({
                        min: value.startOf('month').toDate(),
                        max: value.endOf('month').toDate(),
                      });
                      reset();
                    }
                  }}
                />
              </div>
            </ToolTip>
          </div>
          {isPCCActionAvailable ? (
            <Button
              variant="outlined"
              className="!h-8"
              onClick={handleOpenPCCSettings}
              startIcon={<SettingsIcon className="!h-4 !w-4 !text-inherit" />}
            >
              Settings
            </Button>
          ) : null}
        </div>
      </div>
      <DataProvider<
        PCCPatientDetailsResponseDTO,
        PCCOverviewRow,
        PCCDashboardPillCountsDTO
      >
        queryKey="pcc-overview-patients"
        pagination={defaultPagination}
        getData={postApiPCCDashboardGetPCCPatientDetails}
        getMeta={(response) => (response?.patients?.pageProperties ?? {})}
        getAuxiliary={(response) => response?.pillCounts}
        getQuery={({
          dateRange: queryDateRange,
          searchQuery: querySearch,
          selectedPill: querySelectedPill,
          pagination,
        }) => ({
          // BE ignores it for EligibleNotEnrolled pill, but it is required for all pills
          dateRange: serializeDateRange(queryDateRange),
          searchText: querySearch,
          hasSearchText: notEmptyString(querySearch),
          pillFilter: querySelectedPill.id,
          pageRequest: {
            page: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 25,
          },
        } satisfies PCCPatientDetailsRequestDTO)}
        normalize={(response) => normalizePCCOverviewRows(response?.patients?.data ?? [])}
        params={{
          dateRange,
          searchQuery,
          selectedPill,
        }}
      >
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          selectedPill={selectedPill}
          dateRange={dateRange}
          onSelect={onSelectSummaryCard}
        />
      </DataProvider>
    </div>
  );
}
